import { z } from 'zod';

// ─── member-migration vocabulary ─────────────────────────────────────────────
//
// Governed enums for the Pelago 1.0 → Galapagos member migration: the
// per-member migration marker and the per-step ledger consumers use as their
// idempotency key. There is no engineering-held cohort list — a legacy
// identity is resolved live from backend2 by the migrate flow.
//
// This module is deliberately zod-only so the top-level `@galapagos/shared`
// barrel can export it to every client; the Drizzle tables that render these
// arrays into `text` + CHECK constraints live in `../db/schema.ts` and import
// from here (never the reverse — that edge would be a cycle).

/**
 * Lifecycle shared by the per-member marker and each step row.
 * `pending` → `running` → `done` | `failed`; a failed step may run again.
 */
export const MEMBER_MIGRATION_STATUS = ['pending', 'running', 'done', 'failed'] as const;
export const memberMigrationStatusSchema = z.enum(MEMBER_MIGRATION_STATUS);
export type MemberMigrationStatus = z.infer<typeof memberMigrationStatusSchema>;

/**
 * Step-only lifecycle: the shared set plus `skipped`, a settled outcome for a
 * step that found nothing to do or no destination to write to (its reason code
 * lands in `error`). The member-level marker never skips, so it keeps the
 * shared four-value set. A skipped step is not outstanding for a readiness
 * gate, but a later run may still pick it up: `beginStep` re-runs anything
 * that is not `done`.
 */
export const MEMBER_MIGRATION_STEP_STATUS = [...MEMBER_MIGRATION_STATUS, 'skipped'] as const;
export const memberMigrationStepStatusSchema = z.enum(MEMBER_MIGRATION_STEP_STATUS);
export type MemberMigrationStepStatus = z.infer<typeof memberMigrationStepStatusSchema>;

/**
 * Which migration tier a step belongs to. Tier 0 is the synchronous import the
 * member waits on; tier 1 is the asynchronous set a downstream readiness gate
 * waits for; tier 2 is best-effort backfill. Consumers register a step WITH its
 * tier, so the recorder holds the step → tier mapping as data.
 */
export const MEMBER_MIGRATION_TIER = ['tier0', 'tier1', 'tier2'] as const;
export const memberMigrationTierSchema = z.enum(MEMBER_MIGRATION_TIER);
export type MemberMigrationTier = z.infer<typeof memberMigrationTierSchema>;

/**
 * Step names are machine identifiers: lowercase, dot/underscore/hyphen
 * separated, at most 64 characters. Rendered into the `step_name` CHECK.
 */
export const MEMBER_MIGRATION_STEP_NAME_PATTERN = /^[a-z][a-z0-9_.-]{0,63}$/;
export const memberMigrationStepNameSchema = z
  .string()
  .regex(MEMBER_MIGRATION_STEP_NAME_PATTERN, 'must be a lowercase machine step name (max 64)');

/** Bound on the free-text `error` a failed step records (never member data). */
export const MEMBER_MIGRATION_STEP_ERROR_MAX_LENGTH = 2000;
/** Bound on the opaque Tier 0 source-snapshot reference. */
export const MEMBER_MIGRATION_SNAPSHOT_REF_MAX_LENGTH = 1024;

// ─── per-step reconciliation detail ──────────────────────────────────────────
//
// What a step actually did, recorded beside its status so a sampled member's
// rows-read-versus-rows-written question is answerable in SQL rather than from
// a log line that does not name the member.
//
// Both shapes are deliberately narrow, because they are written from inside a
// migration transaction and read by an auditor. A count key is a code, a count
// value is a whole non-negative number, and a warning is a code with an
// optional table-qualified coordinate. The warning pattern is the mechanical
// privacy guard: it admits no whitespace, no `@`, and no free text, so a
// member value, an address or a 1.0 payload cannot reach the column even by
// accident.

/** A count key: a lowercase code, e.g. `source_rows`, `inserted`. */
export const MEMBER_MIGRATION_STEP_COUNT_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
/**
 * A warning: a code, optionally qualified by a coordinate naming WHERE the
 * condition arose (`legacy_instant_missing:users.created`) — never the value
 * found there.
 */
export const MEMBER_MIGRATION_STEP_WARNING_PATTERN = /^[a-z][a-z0-9_]*(:[A-Za-z0-9_.-]+)?$/;

/** Bound on one warning string, counted in code points. */
export const MEMBER_MIGRATION_STEP_WARNING_MAX_LENGTH = 200;
/** How many distinct counts one step may record. */
export const MEMBER_MIGRATION_STEP_COUNTS_MAX_KEYS = 32;
/** How many warnings one step may record. */
export const MEMBER_MIGRATION_STEP_WARNINGS_MAX_ITEMS = 64;

/**
 * Appended when anything was dropped or truncated on the way in, so the loss
 * is itself recorded. Silently shrinking the detail would reproduce, one layer
 * up, the very defect these columns exist to remove.
 */
export const MEMBER_MIGRATION_STEP_DETAIL_TRUNCATED = 'step_detail_truncated';

export const memberMigrationStepCountsSchema = z.record(
  z.string().regex(MEMBER_MIGRATION_STEP_COUNT_KEY_PATTERN, 'must be a lowercase count code'),
  z.number().int().nonnegative(),
);
export type MemberMigrationStepCounts = z.infer<typeof memberMigrationStepCountsSchema>;

export const memberMigrationStepWarningsSchema = z.array(
  z
    .string()
    .max(MEMBER_MIGRATION_STEP_WARNING_MAX_LENGTH)
    .regex(MEMBER_MIGRATION_STEP_WARNING_PATTERN, 'must be a warning code, optionally qualified'),
);
export type MemberMigrationStepWarnings = z.infer<typeof memberMigrationStepWarningsSchema>;
